import { Strategy } from 'crono-fe-common/types/strategy';
import { ClickAwayListener, FormControl, Typography } from '@mui/material';
import { CustomOptionsTooltip } from 'pages/strategy/customOptionsTooltip';
import { colors } from 'crono-fe-common/theme';
import { useCallback, useMemo, useState } from 'react';
import usePatchStrategySettings from 'hooks/services/strategySettings/usePatchStrategySettings';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';
import {
  parseConditionalTypeToString,
  parseTaskTypeToString,
} from 'utils/businessLogic';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import SelectedTemplateIcon from 'crono-fe-common/icons/Icon-Selected-Template';
import { ReactComponent as TopArrowIcon } from 'crono-fe-common/icons/Top-Arrow.svg';
import { ReactComponent as BottomArrowIcon } from 'crono-fe-common/icons/Bottom-Arrow.svg';
import CronoCheckbox from 'crono-fe-common/components/CronoCheckbox';
import {
  StepsForRewriteOptionsWrapper,
  StepsForRewriteSelectWrapper,
} from './style';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';

interface IProps {
  strategy: Strategy | null;
  disableEdit?: boolean;
}

const StepsForRewriteSelect = ({ strategy, disableEdit }: IProps) => {
  const strategyId = strategy?.id;
  const selectedStepsForRewrite =
    strategy?.strategySettings?.selectedStepsForRewrite ?? [];
  const selectableSteps =
    strategy?.steps.filter(
      (step) =>
        (step.template ||
          step.strategyStepTemplates ||
          step.strategyStepConditional?.template ||
          (step.strategyStepConditional?.strategyStepTemplates?.length ?? 0) >
            0) &&
        step.subtype !== TaskTodoSubtype.LinkedinVoiceNote &&
        step.type !== TaskTodoType.CALL,
    ) ?? [];
  const isAllSelected =
    selectedStepsForRewrite.length === selectableSteps.length ||
    selectedStepsForRewrite.length === 0; // when selectedStepsForRewrite is empty - all are selected (default state of each strategySettings)

  const [selectOpened, setSelectOpened] = useState<boolean>(false);

  const { mutate: patchStrategySettings, error: patchStrategySettingsError } =
    usePatchStrategySettings();
  const [atLeastOneSelectedStepError, setAtLeastOneSelectedStepError] =
    useState(false);

  useConditionalSnackBar([
    {
      condition: !!patchStrategySettingsError,
      message:
        getError(patchStrategySettingsError) ??
        'Error during saving changes to rewrite scope',
      severity: 'error',
    },
    {
      condition: atLeastOneSelectedStepError,
      message: 'At least one step should be selected',
      severity: 'error',
    },
  ]);

  const handleClickOnOption = useCallback(
    (stepId: number) => {
      if (!strategyId) return;

      let newSelectedStepsForRewrite: number[];
      if (selectedStepsForRewrite.includes(stepId)) {
        if (selectedStepsForRewrite.length === 1) {
          setAtLeastOneSelectedStepError(true);
          setTimeout(() => setAtLeastOneSelectedStepError(false), 3000);
          return;
        }
        // remove the previous filter if it was already selected
        newSelectedStepsForRewrite = [...selectedStepsForRewrite].filter(
          (op) => op !== stepId,
        );
      } else {
        if (selectedStepsForRewrite.length === 0) {
          // since by default with empty selectedStepsForRewrite we show all steps as selected -> select all except the one that was clicked
          newSelectedStepsForRewrite = selectableSteps
            .filter((step) => step.id !== stepId)
            .map((step) => step.id);

          if (newSelectedStepsForRewrite.length === 0) {
            setAtLeastOneSelectedStepError(true);
            setTimeout(() => setAtLeastOneSelectedStepError(false), 3000);
            return;
          }
        } else {
          newSelectedStepsForRewrite = [...selectedStepsForRewrite, stepId];
        }
      }

      patchStrategySettings({
        strategyId,
        selectedStepsForRewrite: newSelectedStepsForRewrite,
      });
    },
    [selectedStepsForRewrite, patchStrategySettings, strategyId],
  );
  // Helper to extract titles from template arrays
  function extractTitles(
    templates?: Array<{ template?: { title?: string | null } | null }> | null,
  ): string[] {
    return (
      templates
        ?.map(({ template }) => template?.title)
        .filter((title): title is string => Boolean(title)) || []
    );
  }
  const dropdownOptions = useMemo(() => {
    if (!strategy?.steps || strategy.steps.length === 0) return null;

    if (selectableSteps.length === 0)
      return (
        <StepsForRewriteOptionsWrapper>
          <Typography fontSize={14} fontWeight={500}>
            You have no steps with templates
          </Typography>
        </StepsForRewriteOptionsWrapper>
      );

    return (
      <StepsForRewriteOptionsWrapper>
        {selectableSteps.map((step) => {
          const originalIndex = strategy.steps.findIndex(
            (s) => s.id === step.id,
          );
          const templateOfConditional = step.strategyStepConditional?.template;
          const abTestingTemplatesConditional =
            (step.strategyStepConditional?.strategyStepTemplates?.length ?? 0) >
            0
              ? step.strategyStepConditional?.strategyStepTemplates
              : null;

          // Gather all relevant titles
          const baseTitles = step.template?.title ? [step.template.title] : [];
          const strategyTitles = extractTitles(step.strategyStepTemplates);
          const conditionalTitles = templateOfConditional?.title
            ? [templateOfConditional.title]
            : [];
          const abTestingTitles = extractTitles(abTestingTemplatesConditional);

          // Combine and join with semicolons
          const allTemplateNames = [
            ...baseTitles,
            ...strategyTitles,
            ...conditionalTitles,
            ...abTestingTitles,
          ];

          const templateNamesString = allTemplateNames.join('; ');
          return (
            <>
              <div
                key={step.id}
                className="select-option"
                onClick={() => handleClickOnOption(step.id)}
              >
                <FlexDiv justifyContent={'start'} gap={'0px'}>
                  <CronoCheckbox
                    checked={
                      isAllSelected || selectedStepsForRewrite.includes(step.id)
                    }
                  />
                  <Typography fontSize={14} fontWeight={500}>
                    {`Step ${originalIndex + 1} - ${parseTaskTypeToString(step.type, step.subtype)}${templateOfConditional ? `; ${parseConditionalTypeToString(step.strategyStepConditional?.type)}` : ''}`}
                  </Typography>
                </FlexDiv>

                <FlexDiv
                  direction={'row'}
                  justifyContent={'start'}
                  style={{ paddingLeft: '32px' }}
                >
                  <SelectedTemplateIcon color={colors.grey11} size={16} />
                  <Typography fontSize={12} color={colors.grey11}>
                    {templateNamesString}
                  </Typography>
                </FlexDiv>
              </div>
              <hr
                className={'steps-for-rewrite-option-divider'}
                style={{
                  border: 'none',
                  borderTop: `1px solid ${colors.grey444}`,
                  margin: '4px 0',
                  width: '100%',
                }}
              />
            </>
          );
        })}
      </StepsForRewriteOptionsWrapper>
    );
  }, [selectedStepsForRewrite, selectableSteps, strategy, handleClickOnOption]);

  return (
    <ClickAwayListener onClickAway={() => setSelectOpened(false)}>
      <FormControl size="small" fullWidth disabled={disableEdit}>
        {/*"Tooltip that gets the width of the parent"*/}
        <CustomOptionsTooltip
          open={selectOpened}
          title={dropdownOptions}
          maxHeight={'255px'}
          left={0}
          sx={{
            '& .MuiTooltip-tooltip': {
              width: '100%',
              maxWidth: '100%',
              margin: 0,
            },
          }}
          PopperProps={{
            disablePortal: true,
            sx: {
              width: '100%',
              transform: 'translate3d(0px, 40px, 0px) !important',
            },
          }}
        >
          <StepsForRewriteSelectWrapper
            onClick={() => {
              if (disableEdit) return;
              setSelectOpened((prev) => !prev);
            }}
          >
            <Typography
              fontSize={14}
              style={{ flex: 1 }}
              noWrap
              color={colors.black}
            >
              {isAllSelected
                ? 'All steps'
                : `${selectedStepsForRewrite.length} ${selectedStepsForRewrite.length === 1 ? 'step' : 'steps'} selected`}
            </Typography>

            {selectOpened ? (
              <TopArrowIcon className="tag-arrow" />
            ) : (
              <BottomArrowIcon className="tag-arrow" />
            )}
          </StepsForRewriteSelectWrapper>
        </CustomOptionsTooltip>
      </FormControl>
    </ClickAwayListener>
  );
};

export default StepsForRewriteSelect;
